/**
 * Le fil d'activite d'un contact : ses appels et ses SMS, dans l'ordre.
 *
 * L'onglet « Activite » de la fiche contact n'affichait qu'un etat vide, quoi
 * qu'il se soit passe : les appels vivaient dans l'onglet Historique et les
 * SMS dans l'onglet SMS, et rien ne les reunissait. Ce module fait la fusion,
 * a part du rendu, pour que l'ordre et la lecture des directions soient
 * verifiables sans monter la fiche.
 */

export type ActivityKind = 'call' | 'sms';
export type ActivityDirection = 'inbound' | 'outbound';

export interface CallLike {
  id?: string | null;
  direction?: string | null;
  call_status?: string | null;
  created_date?: string | null;
  started_at?: string | null;
  connected_at?: string | null;
  updated_date?: string | null;
  from_number?: string | null;
  agent_number?: string | null;
  caller_id?: string | null;
  prospect_phone?: string | null;
  /** Ce que la carte montre une fois depliee. */
  duration_seconds?: number | null;
  recording_url?: string | null;
  ai_summary?: string | null;
  transcription?: string | null;
  call_outcome?: string | null;
}

export interface SmsLike {
  direction?: string | null;
  message?: string | null;
  /** L'autre bout de la conversation, tel que la fiche le charge deja. */
  to?: string | null;
  timestamp?: string | null;
  status?: string | null;
}

export interface ActivityEntry {
  /** Stable d'un rendu a l'autre : c'est la cle de depliage. */
  id: string;
  kind: ActivityKind;
  direction: ActivityDirection;
  /** La date affichee et celle qui sert au tri. */
  at: string;
  /** Qui appelle ou ecrit, et qui recoit. */
  from: string;
  to: string;
  call?: CallLike;
  sms?: SmsLike;
}

/** Un appel entrant se reconnait a son champ `direction`, sinon a son statut. */
export function callDirection(call: CallLike): ActivityDirection {
  if (call.direction === 'inbound' || call.direction === 'outbound') return call.direction;
  return call.call_status === 'inbound' ? 'inbound' : 'outbound';
}

/**
 * La date d'un appel. Quatre champs peuvent la porter selon l'ancienete de
 * l'enregistrement ; prendre le premier renseigne evite de faire remonter a
 * 1970 les appels qui n'ont pas `created_date`.
 */
export function callDate(call: CallLike): string {
  return call.created_date || call.started_at || call.connected_at || call.updated_date || '';
}

/** Le numero depuis lequel l'appel est parti, sous ses trois noms successifs. */
export function callFromNumber(call: CallLike): string {
  return call.from_number || call.agent_number || call.caller_id || '';
}

function instant(valeur: string): number {
  const t = Date.parse(valeur);
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Les appels et les SMS d'un contact, du plus recent au plus ancien.
 *
 * `notreNumero` sert de repli quand l'appel ne dit pas d'ou il partait : la
 * carte affiche toujours « de → vers », et une moitie vide se lirait comme
 * une donnee manquante alors que le numero est connu.
 */
export function buildActivityFeed(
  calls: CallLike[],
  sms: SmsLike[],
  prospectPhone: string,
  notreNumero = '',
): ActivityEntry[] {
  const entrees: ActivityEntry[] = [];

  calls.forEach((call, index) => {
    const direction = callDirection(call);
    const nous = callFromNumber(call) || notreNumero;
    const eux = call.prospect_phone || prospectPhone;
    entrees.push({
      id: `call:${call.id ?? index}`,
      kind: 'call',
      direction,
      at: callDate(call),
      from: direction === 'inbound' ? eux : nous,
      to: direction === 'inbound' ? nous : eux,
      call,
    });
  });

  sms.forEach((message, index) => {
    const direction: ActivityDirection = message.direction === 'inbound' ? 'inbound' : 'outbound';
    // `to` porte l'autre bout de la conversation, entrant comme sortant.
    const eux = message.to || prospectPhone;
    entrees.push({
      id: `sms:${message.timestamp ?? ''}:${index}`,
      kind: 'sms',
      direction,
      at: message.timestamp || '',
      from: direction === 'inbound' ? eux : notreNumero,
      to: direction === 'inbound' ? notreNumero : eux,
      sms: message,
    });
  });

  // Tri du plus recent au plus ancien. A date egale, l'ordre d'arrivee est
  // conserve : sans ce depart, deux evenements de la meme seconde
  // changeraient de place d'un rendu a l'autre.
  return entrees
    .map((entree, rang) => ({ entree, rang }))
    .sort((a, b) => instant(b.entree.at) - instant(a.entree.at) || a.rang - b.rang)
    .map(({ entree }) => entree);
}
